import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PermissionGate from "../../../components/common/PermissionGate";
import hrService from "../../../api/services/hrService";
import { useCustomAlert } from "../../../hooks/useCustomAlert";
import CustomAlert from "../../../components/common/CustomAlert";
import {
  ArrowLeft,
  Upload,
  Eye,
  Download,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Image,
  File,
  Plus,
  Search,
  RefreshCw,
  AlertCircle,
} from "lucide-react";

const DocumentManager = () => {
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();

  // State management
  const [documents, setDocuments] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDocumentDetailModal, setShowDocumentDetailModal] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [employeeFullData, setEmployeeFullData] = useState(null);

  // Upload form state
  const [uploadData, setUploadData] = useState({
    name: "",
    type: "",
    file: null,
    description: "",
  });


  // Mock documents data
  const mockDocuments = [
    {
      id: 1,
      name: "Resume_John_Smith.pdf",
      type: "Resume",
      status: "Approved",
      uploadedDate: "2024-01-15",
      size: "2.5 MB",
      uploadedBy: "John Smith",
      approvedBy: "Sarah Johnson",
      approvedDate: "2024-01-16",
      description: "Updated resume with latest experience",
      fileType: "pdf",
    },
    {
      id: 2,
      name: "PAN_Card_John_Smith.pdf",
      type: "ID Proof",
      status: "Approved",
      uploadedDate: "2024-01-10",
      size: "1.2 MB",
      uploadedBy: "John Smith",
      approvedBy: "HR Team",
      approvedDate: "2024-01-11",
      description: "PAN card copy for verification",
      fileType: "pdf",
    },
    {
      id: 3,
      name: "Aadhaar_Card_John_Smith.jpg",
      type: "ID Proof",
      status: "Pending",
      uploadedDate: "2024-01-20",
      size: "1.8 MB",
      uploadedBy: "John Smith",
      approvedBy: null,
      approvedDate: null,
      description: "Aadhaar card front and back",
      fileType: "jpg",
    },
    {
      id: 4,
      name: "Offer_Letter_Agrosaf.pdf",
      type: "Employment",
      status: "Approved",
      uploadedDate: "2022-01-08",
      size: "3.1 MB",
      uploadedBy: "HR Team",
      approvedBy: "HR Manager",
      approvedDate: "2022-01-08",
      description: "Original offer letter",
      fileType: "pdf",
    },
    {
      id: 5,
      name: "Experience_Certificate_ABC_Company.pdf",
      type: "Experience",
      status: "Rejected",
      uploadedDate: "2024-01-18",
      size: "2.2 MB",
      uploadedBy: "John Smith",
      approvedBy: "HR Team",
      approvedDate: "2024-01-19",
      description: "Previous company experience certificate",
      fileType: "pdf",
    },
    {
      id: 6,
      name: "Bank_Statement_January_2024.pdf",
      type: "Financial",
      status: "Approved",
      uploadedDate: "2024-02-01",
      size: "1.5 MB",
      uploadedBy: "John Smith",
      approvedBy: "Finance Team",
      approvedDate: "2024-02-02",
      description: "Bank statement for salary account verification",
      fileType: "pdf",
    },
  ];

  // Document types
  const documentTypes = [
    "Resume",
    "ID Proof",
    "Employment",
    "Experience",
    "Financial",
    "Medical",
    "Educational",
    "Legal",
    "Other",
  ];

  // Status options
  const statusOptions = [
    { value: "all", label: "All Documents" },
    { value: "approved", label: "Approved" },
    { value: "pending", label: "Pending" },
    { value: "rejected", label: "Rejected" },
  ];

  // Check authentication token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No authentication token found. Please login again.");
      // Optionally redirect to login
      // window.location.href = '/login';
    }
  }, []);

  // Load employee and documents
  useEffect(() => {
    loadEmployeeData();
    loadDocuments();
  }, [employeeId]);

  const loadEmployeeData = async () => {
    try {
      const result = await hrService.getEmployeeById(employeeId);

      if (result.success) {
        const emp = result.data;

        // Store full employee data for document details
        setEmployeeFullData(emp);

        // Transform API data to match UI structure
        const employeeData = {
          id: emp._id,
          employeeId: emp.employeeId,
          name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim().replace(/\b\w/g, l => l.toUpperCase()),
          department: emp.employmentInfo?.department || '',
          role: emp.employmentInfo?.position || '',
          email: emp.contactInfo?.personalEmail || emp.user?.email || '',
          phone: emp.contactInfo?.personalPhone || emp.user?.phone || ''
        };

        setEmployee(employeeData);
      } else {
        // Use fallback data
        setEmployee({
          id: employeeId,
          employeeId: employeeId,
          name: 'Employee',
          department: 'Department',
          role: 'Role',
          email: '',
          phone: ''
        });
      }
    } catch (err) {
      // Use fallback data
      setEmployee({
        id: employeeId,
        employeeId: employeeId,
        name: 'Employee',
        department: 'Department',
        role: 'Role',
        email: '',
        phone: ''
      });
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      // Call the actual API
      const result = await hrService.getEmployeeDocuments(employeeId);

      if (result.success) {
        setDocuments(result.data || []);
      } else {
        setError(result.error);
        setDocuments(mockDocuments);
      }
    } catch (err) {
      setError("Failed to load documents. Please try again.");
      setDocuments(mockDocuments);
    } finally {
      setLoading(false);
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || doc.status.toLowerCase() === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (!allowedTypes.includes(file.type)) {
        showError("Please upload a valid file (PDF, DOC, DOCX, JPG, PNG)");
        return;
      }

      if (file.size > maxSize) {
        showError("File size must be less than 10MB");
        return;
      }

      setUploadData((prev) => ({
        ...prev,
        file: file,
        name: file.name,
      }));
    }
  };

  // Handle upload form submission
  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadData.file || !uploadData.type) {
      showError("Please select a file and document type");
      return;
    }

    // Check if token exists
    let token =
      localStorage.getItem("authToken") ||
      localStorage.getItem("auth_token") ||
      localStorage.getItem("token");
    if (!token) {
      token =
        sessionStorage.getItem("authToken") ||
        sessionStorage.getItem("auth_token") ||
        sessionStorage.getItem("token");
    }

    if (!token) {
      showError("No authentication token found. Please login again.");
      return;
    }

    setLoading(true);

    try {
      // Prepare document data
      const documentData = {
        name: uploadData.name,
        type: uploadData.type,
        description: uploadData.description,
        file: uploadData.file, // In real implementation, this would be handled as multipart/form-data
      };

      // Call the actual API
      const result = await hrService.uploadEmployeeDocument(
        employeeId,
        documentData
      );

      if (result.success) {
        // Reload documents to show the new one
        await loadDocuments();

        setShowUploadModal(false);
        setUploadData({ name: "", type: "", file: null, description: "" });

        showSuccess("Document uploaded successfully!");
      } else {
        showError(`Failed to upload document: ${result.error}`);
      }
    } catch (err) {
      // Check if it's an authentication error
      if (err.message && err.message.includes("Session expired")) {
        showError("Your session has expired. Please login again to continue.");
      } else {
        showError("Failed to upload document. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle document actions
  const handleViewDocument = (doc) => {
    // Set the document to view and show detail modal directly
    setViewingDocument(doc);
    setShowDocumentDetailModal(true);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      showSuccess(`Preparing download for ${doc.name}...`);

      // Get fileUrl from doc (prioritize fileUrl, then url)
      let fileUrl = doc.fileUrl || (doc.url && doc.url !== "#" ? doc.url : null);

      // Check if documentData exists and parse it to extract fileUrl
      if (!fileUrl && doc.documentData) {
        try {
          let documentDataParsed;
          if (typeof doc.documentData === 'string') {
            documentDataParsed = JSON.parse(doc.documentData);
          } else {
            documentDataParsed = doc.documentData;
          }

          if (documentDataParsed && documentDataParsed.fileUrl) {
            fileUrl = documentDataParsed.fileUrl;
          }
        } catch (parseError) {
        }
      }

      // Handle base64 data URLs directly in the frontend
      if (fileUrl && fileUrl.startsWith('data:')) {
        try {
          // Parse data URL: data:image/png;base64,iVBORw0KGgo...
          const matches = fileUrl.match(/^data:([^;]+);base64,(.+)$/);
          if (matches && matches.length === 3) {
            const mimeType = matches[1];
            const base64Data = matches[2];

            // Convert base64 to blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Determine file extension from mime type
            let extension = '';
            if (mimeType.includes('png')) extension = '.png';
            else if (mimeType.includes('jpeg') || mimeType.includes('jpg')) extension = '.jpg';
            else if (mimeType.includes('pdf')) extension = '.pdf';
            else if (mimeType.includes('gif')) extension = '.gif';
            else if (mimeType.includes('webp')) extension = '.webp';

            // Get filename
            const filename = (doc.name || doc.originalName || doc.filename || 'document') +
              (extension && !(doc.name || doc.originalName || doc.filename || '').includes('.') ? extension : '');

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSuccess(`Downloaded ${doc.name} successfully!`);
            return;
          }
        } catch (base64Error) {
          // Fall through to API download
        }
      }

      // Check if document has Cloudinary URL or HTTP URL
      if (fileUrl && (fileUrl.includes('cloudinary.com') || fileUrl.startsWith('http'))) {
        window.open(fileUrl, '_blank');
        showSuccess(`Opening ${doc.name} in new tab...`);
        return;
      }

      // Use API download endpoint
      const documentId = doc._id || doc.id || doc.filename;
      const result = await hrService.downloadEmployeeDocument(employeeId, documentId);

      if (result.success) {
        showSuccess(result.message || `Downloaded ${doc.name} successfully!`);
      } else {
        showError(result.error || `Failed to download ${doc.name}`);
      }
    } catch (error) {
      showError(`Failed to download ${doc.name}. Please try again.`);
    }
  };

  const handleReplaceDocument = (doc) => {
    // Check if it's a compliance document (PAN, Aadhaar)
    if (doc.type === "Compliance" || doc.id === "pan" || doc.id === "aadhaar") {
      alert(
        "Compliance documents (PAN, Aadhaar) cannot be replaced. Please update the employee profile to modify these details."
      );
      return;
    }

    setSelectedDocument(doc);
    setUploadData({
      name: doc.name,
      type: doc.type,
      file: null,
      description: doc.description,
    });
    setShowUploadModal(true);
  };

  const handleDeleteDocument = (doc) => {
    // Check if it's a compliance document (PAN, Aadhaar)
    if (doc.type === "Compliance" || doc.id === "pan" || doc.id === "aadhaar") {
      alert(
        "Compliance documents (PAN, Aadhaar) cannot be deleted as they are required for employee records."
      );
      return;
    }

    setSelectedDocument(doc);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedDocument) return;

    setLoading(true);

    try {
      // Call the actual API
      const result = await hrService.deleteEmployeeDocument(
        employeeId,
        selectedDocument.id
      );

      if (result.success) {
        // Remove the document from the list
        setDocuments((prev) =>
          prev.filter((doc) => doc.id !== selectedDocument.id)
        );
        setShowDeleteModal(false);
        setSelectedDocument(null);

        alert("Document deleted successfully!");
      } else {
        alert(`Failed to delete document: ${result.error}`);
      }
    } catch (err) {
      alert("Failed to delete document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Get status color and icon
  const getStatusInfo = (status) => {
    switch (status) {
      case "Approved":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "Pending":
        return { color: "bg-yellow-100 text-yellow-800", icon: Clock };
      case "Rejected":
        return { color: "bg-red-100 text-red-800", icon: XCircle };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: Clock };
    }
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return FileText;
      case "jpg":
      case "jpeg":
      case "png":
        return Image;
      default:
        return File;
    }
  };

  // Loading state
  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/employee_profile/${employeeId}`)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Profile
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Manager</h1>
            <p className="text-gray-600 mt-1">Manage documents for {employee?.name || 'Employee'}</p>
          </div>
        </div>
        <PermissionGate permission="employee_management" action="create">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Upload Document
          </button>
        </PermissionGate>
      </div>

      {/* Employee Info Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-agrosaf-green rounded-full flex items-center justify-center">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{employee?.name || 'Employee'}</h3>
            <p className="text-sm text-gray-600">{employee?.role || 'Role'} • {employee?.department || 'Department'}</p>
            <p className="text-xs text-gray-500">Employee ID: {employee?.employeeId || employeeId}</p>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
              />
            </div>
          </div>
          <div className="md:w-64">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={loadDocuments}
            className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Documents ({filteredDocuments.length})
          </h3>
        </div>

        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No documents found
            </h3>
            <p className="text-gray-600">Upload documents to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredDocuments.map((document) => {
              const StatusIcon = getStatusInfo(document.status).icon;
              const FileIcon = getFileIcon(document.fileType);

              return (
                <div
                  key={document.id}
                  className="p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <FileIcon className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {document.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          {document.type} • {document.uploadedDate}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusInfo(document.status).color
                          }`}
                      >
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {document.status}
                      </span>

                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="employee_management" action="read">
                          <button
                            onClick={() => handleViewDocument(document)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="employee_management" action="read">
                          <button
                            onClick={() => handleDownloadDocument(document)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="employee_management" action="update">
                          <button
                            onClick={() => handleReplaceDocument(document)}
                            className="text-gray-400 hover:text-[#0F2942] transition-colors"
                            title="Replace"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="employee_management" action="delete">
                          <button
                            onClick={() => handleDeleteDocument(document)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedDocument ? "Replace Document" : "Upload New Document"}
            </h3>

            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadData.type}
                  onChange={(e) =>
                    setUploadData((prev) => ({ ...prev, type: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  required
                >
                  <option value="">Select document type</option>
                  {documentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z0-9\s,]/g, '').slice(0, 500);
                    setUploadData((prev) => ({
                      ...prev,
                      description: value,
                    }));
                  }}
                  onKeyPress={handleTextKeyPress}
                  maxLength={500}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                  placeholder="Optional description..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {uploadData.description.length}/500 characters
                </p>
              </div>

              <div className="flex items-center justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedDocument(null);
                    setUploadData({
                      name: "",
                      type: "",
                      file: null,
                      description: "",
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <PermissionGate permission="employee_management" action={selectedDocument ? "update" : "create"}>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {selectedDocument ? "Replace" : "Upload"}
                      </>
                    )}
                  </button>
                </PermissionGate>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Detail Modal */}
      {showDocumentDetailModal && viewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    {(() => {
                      const FileIcon = getFileIcon(viewingDocument.fileType);
                      return <FileIcon className="h-5 w-5 text-gray-600" />;
                    })()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {viewingDocument.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {viewingDocument.type} • {viewingDocument.uploadedDate}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDocumentDetailModal(false);
                    setViewingDocument(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {/* Document Details */}
              <div className="space-y-4">
                {/* Status */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <span
                    className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusInfo(viewingDocument.status).color
                      }`}
                  >
                    {(() => {
                      const StatusIcon = getStatusInfo(viewingDocument.status).icon;
                      return (
                        <>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {viewingDocument.status}
                        </>
                      );
                    })()}
                  </span>
                </div>

                {/* Document Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Extract PAN Number from description or employee data */}
                  {(() => {
                    let panNumber = null;
                    if (viewingDocument.id === 'pan' && employeeFullData?.complianceInfo?.pan) {
                      panNumber = employeeFullData.complianceInfo.pan;
                    } else if (viewingDocument.description?.includes('PAN Number:')) {
                      panNumber = viewingDocument.description.split('PAN Number:')[1]?.trim();
                    } else if (viewingDocument.panNumber) {
                      panNumber = viewingDocument.panNumber;
                    }
                    return panNumber ? (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-500 block mb-1">PAN Number:</span>
                        <span className="text-sm font-medium text-gray-900">{panNumber}</span>
                      </div>
                    ) : null;
                  })()}

                  {/* Extract Aadhaar Number from description or employee data */}
                  {(() => {
                    let aadhaarNumber = null;
                    if (viewingDocument.id === 'aadhaar' && employeeFullData?.complianceInfo?.aadhaar) {
                      aadhaarNumber = employeeFullData.complianceInfo.aadhaar;
                    } else if (viewingDocument.description?.includes('Aadhaar Number:')) {
                      aadhaarNumber = viewingDocument.description.split('Aadhaar Number:')[1]?.trim();
                    } else if (viewingDocument.aadhaarNumber) {
                      aadhaarNumber = viewingDocument.aadhaarNumber;
                    }
                    return aadhaarNumber ? (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <span className="text-xs text-gray-500 block mb-1">Aadhaar Number:</span>
                        <span className="text-sm font-medium text-gray-900">{aadhaarNumber}</span>
                      </div>
                    ) : null;
                  })()}

                  {viewingDocument.size && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">File Size:</span>
                      <span className="text-sm font-medium text-gray-900">{viewingDocument.size}</span>
                    </div>
                  )}
                  {viewingDocument.fileType && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">File Type:</span>
                      <span className="text-sm font-medium text-gray-900 uppercase">{viewingDocument.fileType}</span>
                    </div>
                  )}
                </div>

                {/* Description - Only show if it's not just PAN/Aadhaar info */}
                {viewingDocument.description &&
                  !viewingDocument.description.includes('PAN Number:') &&
                  !viewingDocument.description.includes('Aadhaar Number:') && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-500 block mb-1">Description:</span>
                      <span className="text-sm text-gray-900">{viewingDocument.description}</span>
                    </div>
                  )}

                {/* Uploaded By */}
                {viewingDocument.uploadedBy && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Uploaded by:</span>
                    <span className="text-sm font-medium text-gray-900">{viewingDocument.uploadedBy}</span>
                  </div>
                )}

                {/* Approved By */}
                {viewingDocument.approvedBy && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-500 block mb-1">Approved by:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {viewingDocument.approvedBy}
                      {viewingDocument.approvedDate && ` on ${viewingDocument.approvedDate}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowDocumentDetailModal(false);
                    setViewingDocument(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <PermissionGate permission="employee_management" action="read">
                  <button
                    onClick={() => {
                      if (viewingDocument.url && viewingDocument.url !== "#") {
                        window.open(viewingDocument.url, "_blank");
                      } else if (viewingDocument.fileUrl) {
                        window.open(viewingDocument.fileUrl, "_blank");
                      } else {
                        handleDownloadDocument(viewingDocument);
                      }
                    }}
                    className="px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033] transition-colors flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Delete Document
              </h3>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedDocument.name}"? This
              action cannot be undone.
            </p>

            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedDocument(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <PermissionGate permission="employee_management" action="delete">
                <button
                  onClick={confirmDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </>
                  )}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default DocumentManager;
